import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Button,
  Typography,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Save,
  Publish,
  Visibility,
  Add,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import SectionEditor from "../SectionEditor";
import PublishConfirmationModal from "../PublishConfirmationModal";
import VersionHistory from "../VersionHistory";
import RollbackConfirmationModal from "../RollbackConfirmationModal";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
  },
  unsavedIndicator: {
    marginLeft: theme.spacing(2),
  },
  tabPanel: {
    marginTop: theme.spacing(2),
  },
  sectionsContainer: {
    marginTop: theme.spacing(2),
  },
  addSectionButton: {
    marginTop: theme.spacing(2),
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
}));

const LandingPageEditor = ({
  initialContent,
  onSave,
  onPublish,
  onPreview,
}) => {
  const classes = useStyles();
  const [content, setContent] = useState(initialContent);
  const [activeTab, setActiveTab] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [showRollbackModal, setShowRollbackModal] = useState(false);
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  // Auto-save cada 30 segundos
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      handleSave(true);
    }, 30000);

    return () => clearTimeout(autoSaveTimer);
  }, [content, hasUnsavedChanges]);

  // Cargar versiones cuando se abre el tab de historial
  useEffect(() => {
    if (activeTab === 6) {
      fetchVersions();
    }
  }, [activeTab]);

  const fetchVersions = async () => {
    try {
      setLoadingVersions(true);
      const { data } = await api.get("/landing-page/versions");
      setVersions(data);
    } catch (err) {
      console.error("Error fetching versions:", err);
      toast.error("Error al cargar el historial de versiones");
    } finally {
      setLoadingVersions(false);
    }
  };

  const handleContentChange = useCallback((newContent) => {
    setContent(newContent);
    setHasUnsavedChanges(true);
  }, []);

  const handleSectionChange = (index, updatedSection) => {
    const newSections = [...content.sections];
    newSections[index] = updatedSection;
    handleContentChange({
      ...content,
      sections: newSections,
    });
  };

  const handleSectionDelete = (index) => {
    const newSections = content.sections.filter((_, i) => i !== index);
    handleContentChange({
      ...content,
      sections: newSections,
    });
  };

  const handleSectionToggleActive = (index) => {
    const newSections = [...content.sections];
    newSections[index] = {
      ...newSections[index],
      active: !newSections[index].active,
    };
    handleContentChange({
      ...content,
      sections: newSections,
    });
  };

  const handleAddSection = (type) => {
    const newSection = {
      id: `${type}-${Date.now()}`,
      type,
      order: content.sections.length,
      active: true,
      data: getDefaultSectionData(type),
    };
    handleContentChange({
      ...content,
      sections: [...content.sections, newSection],
    });
  };

  const getDefaultSectionData = (type) => {
    switch (type) {
      case "navigation":
        return {
          logo: { imageUrl: "", altText: "", linkUrl: "/" },
          links: [],
          ctaButton: { text: "", url: "", style: "primary" },
          sticky: true,
        };
      case "hero":
        return {
          title: "",
          subtitle: "",
          backgroundImage: "",
          cta: { text: "", url: "", style: "primary" },
        };
      case "features":
        return {
          title: "",
          features: [],
        };
      case "testimonials":
        return {
          title: "",
          testimonials: [],
        };
      case "cta":
        return {
          title: "",
          description: "",
          primaryCta: { text: "", url: "" },
        };
      case "footer":
        return {
          logo: { imageUrl: "", altText: "" },
          columns: [],
          socialLinks: [],
          copyright: "",
          bottomLinks: [],
        };
      default:
        return {};
    }
  };

  const handleSave = async (isAutoSave = false) => {
    setSaving(true);
    try {
      await onSave(content);
      setHasUnsavedChanges(false);
      if (!isAutoSave) {
        toast.success("Cambios guardados exitosamente");
      }
    } catch (error) {
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (hasUnsavedChanges) {
      toast.warning("Debes guardar los cambios antes de publicar");
      return;
    }

    setShowPublishModal(true);
  };

  const handleConfirmPublish = async () => {
    setShowPublishModal(false);
    setPublishing(true);
    try {
      await onPublish();
      const now = new Date().toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      });
      toast.success(`Landing page publicada exitosamente - ${now}`);
      // Recargar versiones después de publicar
      if (activeTab === 6) {
        fetchVersions();
      }
    } catch (error) {
      toast.error("Error al publicar la landing page");
    } finally {
      setPublishing(false);
    }
  };

  const handlePreviewVersion = (version) => {
    // Abrir preview de una versión específica en nueva ventana
    const previewWindow = window.open("/admin/landing-page/preview", "_blank");
    // Nota: En una implementación completa, pasaríamos el versionId como parámetro
    toast.info("Abriendo vista previa de la versión...");
  };

  const handleRestoreVersion = (versionId) => {
    setSelectedVersionId(versionId);
    setShowRollbackModal(true);
  };

  const handleConfirmRollback = async () => {
    setShowRollbackModal(false);
    try {
      await api.post(`/landing-page/rollback/${selectedVersionId}`);
      toast.success("Versión restaurada exitosamente");
      // Recargar versiones y contenido
      fetchVersions();
      window.location.reload(); // Recargar para obtener el nuevo contenido
    } catch (error) {
      console.error("Error during rollback:", error);
      toast.error("Error al restaurar la versión");
    } finally {
      setSelectedVersionId(null);
    }
  };

  const handlePreview = () => {
    if (hasUnsavedChanges) {
      toast.info("Guardando cambios antes de previsualizar...");
      handleSave().then(() => {
        onPreview();
      });
    } else {
      onPreview();
    }
  };

  const getSectionsByType = (type) => {
    return content.sections
      .map((section, index) => ({ section, index }))
      .filter(({ section }) => section.type === type);
  };

  const renderSectionsByType = (type) => {
    const sections = getSectionsByType(type);
    if (sections.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No hay secciones de este tipo. Haz clic en "Agregar sección" para crear una.
        </Typography>
      );
    }
    return sections.map(({ section, index }) => (
      <SectionEditor
        key={section.id}
        section={section}
        onChange={(updated) => handleSectionChange(index, updated)}
        onDelete={() => handleSectionDelete(index)}
        onToggleActive={() => handleSectionToggleActive(index)}
      />
    ));
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5">Editor de Landing Page</Typography>
          {hasUnsavedChanges && (
            <Chip
              label="Cambios sin guardar"
              color="secondary"
              size="small"
              className={classes.unsavedIndicator}
            />
          )}
        </Box>
        <Box className={classes.actions}>
          <Button
            variant="outlined"
            startIcon={<Visibility />}
            onClick={handlePreview}
          >
            Vista Previa
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={20} /> : <Save />}
            onClick={() => handleSave(false)}
            disabled={saving || !hasUnsavedChanges}
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={publishing ? <CircularProgress size={20} /> : <Publish />}
            onClick={handlePublish}
            disabled={publishing || hasUnsavedChanges}
          >
            Publicar
          </Button>
        </Box>
      </Box>

      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab label="Navegación" />
          <Tab label="Hero" />
          <Tab label="Características" />
          <Tab label="Testimonios" />
          <Tab label="CTA" />
          <Tab label="Footer" />
          <Tab label="Historial" />
        </Tabs>

        <Box className={classes.tabPanel}>
          {activeTab === 0 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("navigation")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("navigation")}
                className={classes.addSectionButton}
              >
                Agregar sección de navegación
              </Button>
            </Box>
          )}
          {activeTab === 1 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("hero")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("hero")}
                className={classes.addSectionButton}
              >
                Agregar sección hero
              </Button>
            </Box>
          )}
          {activeTab === 2 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("features")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("features")}
                className={classes.addSectionButton}
              >
                Agregar sección de características
              </Button>
            </Box>
          )}
          {activeTab === 3 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("testimonials")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("testimonials")}
                className={classes.addSectionButton}
              >
                Agregar sección de testimonios
              </Button>
            </Box>
          )}
          {activeTab === 4 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("cta")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("cta")}
                className={classes.addSectionButton}
              >
                Agregar sección CTA
              </Button>
            </Box>
          )}
          {activeTab === 5 && (
            <Box className={classes.sectionsContainer}>
              {renderSectionsByType("footer")}
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => handleAddSection("footer")}
                className={classes.addSectionButton}
              >
                Agregar footer
              </Button>
            </Box>
          )}
          {activeTab === 6 && (
            <Box className={classes.sectionsContainer}>
              {loadingVersions ? (
                <Box className={classes.loading}>
                  <CircularProgress />
                </Box>
              ) : (
                <VersionHistory
                  versions={versions}
                  onRestore={handleRestoreVersion}
                  onPreview={handlePreviewVersion}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      <PublishConfirmationModal
        open={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handleConfirmPublish}
        changes={[
          `${content.sections.filter((s) => s.active).length} secciones activas`,
          "Cambios en contenido y configuración",
        ]}
      />

      <RollbackConfirmationModal
        open={showRollbackModal}
        onClose={() => {
          setShowRollbackModal(false);
          setSelectedVersionId(null);
        }}
        onConfirm={handleConfirmRollback}
        version={versions.find((v) => v.id === selectedVersionId)}
      />
    </Box>
  );
};

export default LandingPageEditor;
